import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMap } from '@vis.gl/react-google-maps';

import { toLatLng } from "utils/map";
import { TouchableWithoutFeedback, View } from "react-native";

/* **** */
/* This IS the web version of BaseMarker. */
/* **** */

export const BaseMarker = ({ coordinate, onPress, style, children }) => {
    const map = useMap();
    const overlayRef = useRef(null);
    const [container, setContainer] = useState(null);

    useEffect(() => {
        if (!map || !window.google) return;

        // Create a custom overlay
        class CustomOverlay extends window.google.maps.OverlayView {
            constructor(position, zIndex) {
                super();
                this.position = position;
                this.zIndex = zIndex;
                this.div = null;
            }

            onAdd() {
                const panes = this.getPanes();
                const div = document.createElement('div');
                div.style.position = 'absolute';
                div.style.zIndex = this.zIndex || 0;
                this.div = div;
                panes.overlayMouseTarget.appendChild(div);
                setContainer(div);
            }

            draw() {
                if (!this.div) return;

                const projection = this.getProjection();
                if (!projection) return;

                const point = projection.fromLatLngToDivPixel(this.position);

                if (point) {
                    this.div.style.left = point.x + 'px';
                    this.div.style.top = point.y + 'px';
                }
            }

            onRemove() {
                if (this.div) {
                    setContainer(null);
                    // Use setTimeout to avoid synchronous unmount
                    setTimeout(() => {
                        if (this.div && this.div.parentNode) {
                            this.div.parentNode.removeChild(this.div);
                        }
                        this.div = null;
                    }, 0);
                }
            }
        }

        const position = new window.google.maps.LatLng(
            toLatLng(coordinate).lat,
            toLatLng(coordinate).lng
        );

        const overlay = new CustomOverlay(position, style?.zIndex);
        overlay.setMap(map);
        overlayRef.current = overlay;

        return () => {
            if (overlayRef.current) {
                overlayRef.current.setMap(null);
            }
        };
    }, [map, coordinate, style?.zIndex]);

    // Redraw when coordinate changes
    useEffect(() => {
        if (overlayRef.current) {
            overlayRef.current.position = new window.google.maps.LatLng(
                toLatLng(coordinate).lat,
                toLatLng(coordinate).lng
            );
            overlayRef.current.draw();
        }
    }, [coordinate]);

    if (!container) return null;

    return createPortal(
        <TouchableWithoutFeedback onPress={onPress && onPress}>
            <View style={{ ...style }}>
                {children}
            </View>
        </TouchableWithoutFeedback>,
        container
    );
}